<script setup lang="ts">
import { ArrowLeft, ArrowRight, CircleArrowLeft, CircleArrowRight } from '@lucide/vue'
import { ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { SuccessorRoute, TaskLink } from '@/api'

/*
 * The lane, each linked task and each outcome chip are kit parts (#1227): a kit surface, an interactive kit
 * surface that takes the command cyan on hover, and the kit system label. The two lanes are told apart by their
 * heading, arrow and side, not by a colour stated here.
 */

const props = defineProps<{
    /** Which side of the flow this lane shows: what runs before this task, or what runs after it. */
    variant: 'predecessor' | 'successor'
    /** Successors carry the outcomes that lead to them; predecessors do not. */
    links: Array<TaskLink & { routes?: SuccessorRoute[] }>
}>()

/** `ci_pending · after 3 min · max 30 runs`: the outcome first, then whatever narrows or delays it. */
function routeLabel(route: SuccessorRoute): string {
    return [
        route.on,
        route.when ? `when ${route.when}` : null,
        route.delayMinutes ? `after ${route.delayMinutes} min` : null,
        route.maxRunsPerIssue ? `max ${route.maxRunsPerIssue} runs` : null,
    ]
        .filter(Boolean)
        .join(' · ')
}

/** Both lanes are the same shape, so only the per-variant values live here. */
const lane = computed(() =>
    props.variant === 'predecessor'
        ? {
              testid: 'task-flow-predecessors',
              nodeTestid: 'task-flow-predecessor-node',
              headerTone: 'system' as const,
              headerIcon: CircleArrowLeft,
              label: props.links.length > 1 ? 'Predecessors' : 'Predecessor',
              nodeIcon: ArrowLeft,
              // Predecessors point back at the task, so their arrow leads the row; successors trail it.
              nodeIconLeads: true,
          }
        : {
              testid: 'task-flow-successors',
              nodeTestid: 'task-flow-successor-node',
              headerTone: 'default' as const,
              headerIcon: CircleArrowRight,
              label: props.links.length > 1 ? 'Successors' : 'Successor',
              nodeIcon: ArrowRight,
              nodeIconLeads: false,
          },
)
</script>

<template>
	<ZhPanel v-if="links.length" :data-testid="lane.testid" class="space-y-2">
		<ZhSystemLabel :tone="lane.headerTone" class="flex items-center gap-1.5">
			<component :is="lane.headerIcon" class="size-4" aria-hidden="true" />
			{{ lane.label }}
		</ZhSystemLabel>
		<div class="space-y-1">
			<div v-for="row in links" :key="row.slug" :data-testid="lane.nodeTestid">
				<RouterLink v-if="row.id" v-slot="{ href, navigate }" :to="`/tasks/${row.id}`" custom>
					<ZhPanel as="a" interactive :href="href" class="flex items-center gap-3 text-sm" @click="navigate">
						<component
							:is="lane.nodeIcon"
							v-if="lane.nodeIconLeads"
							class="size-4 shrink-0"
							aria-hidden="true"
						/>
						<span class="min-w-0 space-y-1">
							<span class="block font-medium">{{ row.name || row.slug }}</span>
							<span v-if="row.routes?.length" class="flex flex-wrap gap-1">
								<ZhSystemLabel
									v-for="(route, index) in row.routes"
									:key="index"
									as="span"
									tone="muted"
									data-testid="task-flow-route"
								>{{ routeLabel(route) }}</ZhSystemLabel>
							</span>
						</span>
						<span class="text-muted-foreground ml-auto font-mono text-xs">{{ row.slug }}</span>
						<component
							:is="lane.nodeIcon"
							v-if="!lane.nodeIconLeads"
							class="size-4 shrink-0"
							aria-hidden="true"
						/>
					</ZhPanel>
				</RouterLink>
				<ZhPanel v-else as="p" class="text-muted-foreground flex items-center gap-3 text-sm">
					<span class="font-medium">{{ row.name || row.slug }}</span>
					<span class="ml-auto text-xs">deleted</span>
				</ZhPanel>
			</div>
		</div>
	</ZhPanel>
</template>
